<template>
  <teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <!-- Background overlay -->
        <div
          class="fixed inset-0 bg-gray-500 opacity-70"
          @click="closeModal"
        ></div>

        <!-- Hidden spacer element for proper centering -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >

        <!-- Modal panel -->
        <div
          class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full z-10"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Log Entry Details
              </h3>
              <button
                @click="closeModal"
                class="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <Icon icon="mdi:close" class="h-6 w-6" />
              </button>
            </div>

            <div v-if="log" class="space-y-6">
              <!-- Basic Information -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-gray-900 mb-3">
                    Basic Information
                  </h4>
                  <dl class="space-y-2 text-sm">
                    <div>
                      <dt class="font-medium text-gray-700">ID:</dt>
                      <dd class="text-gray-900">{{ log.id }}</dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-700">Timestamp:</dt>
                      <dd class="text-gray-900">
                        {{ formatDateTime(log.timestamp) }}
                      </dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-700">Source:</dt>
                      <dd>
                        <span
                          class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                          :class="getSourceClass(log.source)"
                        >
                          {{ log.source }}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt class="font-medium text-gray-700">Level:</dt>
                      <dd>
                        <span
                          class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                          :class="getLevelClass(log.level)"
                        >
                          {{ log.level }}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>

                <div class="bg-gray-50 p-4 rounded-lg">
                  <h4 class="font-semibold text-gray-900 mb-3">
                    Event Information
                  </h4>
                  <dl class="space-y-2 text-sm">
                    <div>
                      <dt class="font-medium text-gray-700">Event:</dt>
                      <dd class="text-gray-900 font-mono">{{ log.event }}</dd>
                    </div>
                    <div v-if="log.category">
                      <dt class="font-medium text-gray-700">Category:</dt>
                      <dd class="text-gray-900">{{ log.category }}</dd>
                    </div>
                    <div v-if="log.session_id">
                      <dt class="font-medium text-gray-700">Session ID:</dt>
                      <dd class="text-gray-900 font-mono text-xs">
                        {{ log.session_id }}
                      </dd>
                    </div>
                    <div v-if="log.request_id">
                      <dt class="font-medium text-gray-700">Request ID:</dt>
                      <dd class="text-gray-900 font-mono text-xs">
                        {{ log.request_id }}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              <!-- HTTP Request Details -->
              <div
                v-if="log.endpoint || log.http_method || log.status_code"
                class="bg-blue-50 p-4 rounded-lg"
              >
                <h4 class="font-semibold text-gray-900 mb-3">
                  HTTP Request Details
                </h4>
                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm"
                >
                  <div v-if="log.http_method">
                    <dt class="font-medium text-gray-700">Method:</dt>
                    <dd class="text-gray-900">
                      <span
                        class="inline-flex px-2 py-1 text-xs font-semibold rounded"
                        :class="getHttpMethodClass(log.http_method)"
                      >
                        {{ log.http_method }}
                      </span>
                    </dd>
                  </div>
                  <div v-if="log.endpoint">
                    <dt class="font-medium text-gray-700">Endpoint:</dt>
                    <dd class="text-gray-900 font-mono text-xs">
                      {{ log.endpoint }}
                    </dd>
                  </div>
                  <div v-if="log.status_code">
                    <dt class="font-medium text-gray-700">Status Code:</dt>
                    <dd class="text-gray-900">
                      <span
                        class="inline-flex px-2 py-1 text-xs font-semibold rounded"
                        :class="getStatusCodeClass(log.status_code)"
                      >
                        {{ log.status_code }}
                      </span>
                    </dd>
                  </div>
                  <div v-if="log.duration_ms">
                    <dt class="font-medium text-gray-700">Duration:</dt>
                    <dd class="text-gray-900">{{ log.duration_ms }}ms</dd>
                  </div>
                </div>
              </div>

              <!-- Frontend Details -->
              <div
                v-if="log.page || log.referrer || log.user_agent"
                class="bg-green-50 p-4 rounded-lg"
              >
                <h4 class="font-semibold text-gray-900 mb-3">
                  Frontend Details
                </h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div v-if="log.page">
                    <dt class="font-medium text-gray-700">Page:</dt>
                    <dd class="text-gray-900 font-mono text-xs">
                      {{ log.page }}
                    </dd>
                  </div>
                  <div v-if="log.referrer">
                    <dt class="font-medium text-gray-700">Referrer:</dt>
                    <dd class="text-gray-900 font-mono text-xs">
                      {{ log.referrer }}
                    </dd>
                  </div>
                  <div v-if="log.user_agent">
                    <dt class="font-medium text-gray-700">User Agent:</dt>
                    <dd class="text-gray-900 text-xs break-all">
                      {{ log.user_agent }}
                    </dd>
                  </div>
                  <div v-if="log.ip_address">
                    <dt class="font-medium text-gray-700">IP Address:</dt>
                    <dd class="text-gray-900">{{ log.ip_address }}</dd>
                  </div>
                </div>
              </div>

              <!-- User Information -->
              <div v-if="log.vuo_id" class="bg-purple-50 p-4 rounded-lg">
                <h4 class="font-semibold text-gray-900 mb-3">
                  User Information
                </h4>
                <div class="text-sm">
                  <dt class="font-medium text-gray-700">User ID:</dt>
                  <dd class="text-gray-900">{{ log.vuo_id }}</dd>
                </div>
              </div>

              <!-- Error Information -->
              <div
                v-if="log.error_message || log.error_stack"
                class="bg-red-50 p-4 rounded-lg"
              >
                <h4 class="font-semibold text-red-900 mb-3">
                  Error Information
                </h4>
                <div class="space-y-3">
                  <div v-if="log.error_message">
                    <dt class="font-medium text-red-700">Error Message:</dt>
                    <dd
                      class="text-red-900 font-mono text-sm bg-red-100 p-2 rounded"
                    >
                      {{ log.error_message }}
                    </dd>
                  </div>
                  <div v-if="log.error_stack">
                    <dt class="font-medium text-red-700">Stack Trace:</dt>
                    <dd
                      class="text-red-900 font-mono text-xs bg-red-100 p-2 rounded overflow-x-auto whitespace-pre-wrap"
                    >
                      {{ log.error_stack }}
                    </dd>
                  </div>
                </div>
              </div>

              <!-- Metadata -->
              <div
                v-if="log.meta && Object.keys(log.meta).length > 0"
                class="bg-gray-50 p-4 rounded-lg"
              >
                <h4 class="font-semibold text-gray-900 mb-3">
                  Additional Metadata
                </h4>
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-100">
                      <tr>
                        <th class="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Field</th>
                        <th class="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase">Value</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="(value, key) in log.meta" :key="key" class="hover:bg-gray-50">
                        <td class="px-4 py-2 text-sm font-medium text-gray-700">{{ key }}</td>
                        <td class="px-4 py-2 text-sm text-gray-900">
                          <template v-if="typeof value === 'object' && value !== null">
                            <pre class="text-xs bg-gray-100 p-2 rounded overflow-x-auto">{{ JSON.stringify(value, null, 2) }}</pre>
                          </template>
                          <template v-else>
                            <span class="font-mono text-xs">{{ value }}</span>
                          </template>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="closeModal"
              type="button"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-mocha-mousse-600 text-base font-medium text-white hover:bg-mocha-mousse-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mocha-mousse-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, watch } from "vue";
import { Icon } from "@iconify/vue";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  log: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["close"]);

const closeModal = () => {
  emit("close");
};

const formatDateTime = (timestamp) => {
  return new Date(timestamp).toLocaleDateString("en-GB");
};

const getSourceClass = (source) => {
  switch (source) {
    case "backend":
      return "bg-blue-100 text-blue-800";
    case "frontend":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getLevelClass = (level) => {
  switch (level) {
    case "debug":
      return "bg-gray-100 text-gray-800";
    case "info":
      return "bg-blue-100 text-blue-800";
    case "warning":
      return "bg-yellow-100 text-yellow-800";
    case "error":
      return "bg-red-100 text-red-800";
    case "critical":
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getHttpMethodClass = (method) => {
  switch (method?.toUpperCase()) {
    case "GET":
      return "bg-green-100 text-green-800";
    case "POST":
      return "bg-blue-100 text-blue-800";
    case "PUT":
      return "bg-yellow-100 text-yellow-800";
    case "DELETE":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusCodeClass = (code) => {
  if (code >= 200 && code < 300) {
    return "bg-green-100 text-green-800";
  } else if (code >= 300 && code < 400) {
    return "bg-yellow-100 text-yellow-800";
  } else if (code >= 400) {
    return "bg-red-100 text-red-800";
  }
  return "bg-gray-100 text-gray-800";
};

// Close modal on escape key
const handleEscKey = (e) => {
  if (e.key === "Escape" && props.isOpen) {
    closeModal();
  }
};

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      document.addEventListener("keydown", handleEscKey);
    } else {
      document.removeEventListener("keydown", handleEscKey);
    }
  },
);
</script>
